import { useEffect, useRef, useState } from 'react';
import { PaperPlaneRight, Sparkle, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { streamAI } from '../lib/aiStream';
import { AiText } from '../components/ai/AiText';

const SUGGESTIONS = [
  'Produk mana yang paling untung bulan ini?',
  'Biaya apa yang paling besar dan bagaimana menekannya?',
  'Bahan apa yang perlu segera dibeli?',
  'Saluran jual mana yang paling menguntungkan?',
];

const getSession = () => {
  let id = localStorage.getItem('bumen-ai-session');
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `s${Date.now()}${Math.random().toString(16).slice(2)}`);
    localStorage.setItem('bumen-ai-session', id);
  }
  return id;
};

export default function Asisten() {
  const [session] = useState(getSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/chat/${session}`).then((r) => setMessages((prev) => (prev.length ? prev : r.data))).catch(() => {});
  }, [session]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    const setLast = (content) => setMessages((m) => [...m.slice(0, -1), { role: 'assistant', content }]);
    try {
      await streamAI('/ai/chat', { session_id: session, message: msg }, setLast);
    } catch (e) {
      setLast(`Maaf, terjadi kesalahan: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    await api.delete(`/ai/chat/${session}`);
    setMessages([]);
    toast.success('Percakapan dihapus');
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-170px)] min-h-[480px]">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Tanya apa saja tentang penjualan, laba, stok dan biaya. Claude menjawab dari data Bumen HUB Anda.</p>
        {messages.length > 0 && (
          <button onClick={reset} data-testid="btn-chat-reset" className="shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-border hover:bg-secondary">
            <Trash size={14} /> Hapus
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="bg-card border border-border/60 rounded-xl p-6">
            <div className="flex items-center gap-2 font-display font-semibold"><Sparkle size={20} weight="duotone" className="text-primary" /> Mulai dengan pertanyaan:</div>
            <div className="flex flex-wrap gap-2 mt-4">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}
                  className="text-sm px-3 py-2 rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-card border border-border/60 rounded-bl-sm'}`}>
              {m.role === 'user' ? <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                : m.content ? <AiText text={m.content} /> : <div className="flex gap-1 py-1">{[0, 1, 2].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: `${d * 120}ms` }} />)}</div>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Tulis pertanyaan…" maxLength={2000} data-testid="chat-input"
          className="flex-1 px-4 py-3 bg-card border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40" />
        <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send"
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 hover:opacity-90 transition-opacity">
          <PaperPlaneRight size={20} weight="fill" />
        </button>
      </form>
    </div>
  );
}
